import { AbilityBuilder, createMongoAbility } from '@casl/ability';
import find from 'lodash.find';
import {
  FLEET_MANAGER,
  PBP_CS_TIER_2,
  PBP_CUSTOMER_SUCCESS,
  PBP_FINANCE_ADMIN,
  PBP_SUPER_ADMIN,
  PRIMARY_FLEET_MANAGER,
  REPORTING,
  SERVICE_ADMIN,
  RESTRICTED_CORPORATE_CLIENT_PROSPECT,
} from 'constants.js';
import actions from './actions';

// Detect role by corporation; users may have many corporations with different roles
export const filterRoleByCorp = (role, corporations, corporateClientId, isPbpStaffRole) => {
  if (isPbpStaffRole) return splitRole(role, isPbpStaffRole);
  if (corporateClientId === RESTRICTED_CORPORATE_CLIENT_PROSPECT) return PRIMARY_FLEET_MANAGER;
  if (role.length === 1) return splitRole(role);

  let corporationRole;
  const corporation = find(corporations, { id: corporateClientId });

  if (corporation) {
    const { id } = corporation;
    const corporationMatch = find(role, r => r.split(':')[2] === id);
    corporationRole = splitRole(corporationMatch);
  }
  return corporationRole;
};

export const splitRole = (permission, isPbpStaffRole = false) => {
  let role;
  if (isPbpStaffRole) return permission;

  if (permission) {
    const permissionsIndex = 3;
    role = permission.toString().split(':')[permissionsIndex];
  }
  return role;
};

export const defineRulesFor = (role, corporations, corporateClientId, isPbpStaffRole) => {
  const { can, cannot, rules } = new AbilityBuilder(createMongoAbility);
  const corporationRole = filterRoleByCorp(role, corporations, corporateClientId, isPbpStaffRole);
  const corporation = find(corporations, { id: corporateClientId });

  const disableBulkDriverUploadForSelfRegisteredCorporations = role => {
    corporation?.registrationType === 'SelfRegistered'
      ? cannot(['bulkDriverUpload'], 'RestrictedDriverUpload')
      : role === REPORTING
      ? cannot(['bulkDriverUpload'], 'RestrictedDriverUpload')
      : can(['bulkDriverUpload'], 'RestrictedDriverUpload');
  };

  if (corporationRole === PBP_SUPER_ADMIN) {
    can(actions.pbpSuperAdmin.all, 'all');

    // Vehicle Form
    can(actions.pbpSuperAdmin.vehicleForm, 'VehicleForm');
    // Funding Request
    can(actions.pbpSuperAdmin.fundingRequest, 'FundingRequest');
    // Wallet
    cannot(actions.pbpSuperAdmin.wallet.cannot, 'Wallet');
  }

  if (corporationRole === PBP_FINANCE_ADMIN) {
    can(actions.pbpFinanceAdmin.adminUsers, 'AdminUser');
    // Funding Request
    can(actions.pbpFinanceAdmin.fundingRequest, 'FundingRequest');
    cannot(actions.pbpFinanceAdmin.driver.cannot, 'Driver');
    // Vehicle Form
    cannot(actions.pbpFinanceAdmin.vehicleForm, 'VehicleForm');
    cannot(actions.pbpFinanceAdmin.vehicle.cannot, 'Vehicle');
    cannot(actions.pbpFinanceAdmin.paymentSpa.cannot, 'PaymentSpa');
    cannot(actions.pbpFinanceAdmin.billing.cannot, 'Billing');
    disableBulkDriverUploadForSelfRegisteredCorporations(REPORTING);
    cannot(
      actions.pbpFinanceAdmin.subscriptionInvoicesAnnouncement.cannot,
      'SubscriptionInvoicesAnnouncement',
    );
  }

  if (corporationRole === PRIMARY_FLEET_MANAGER) {
    can(actions.primaryFleetManager.all, 'all');
    // Vehicle
    can(actions.primaryFleetManager.vehicle, 'Vehicle');
    // Driver
    can(actions.primaryFleetManager.driver.can, 'Driver');
    cannot(actions.primaryFleetManager.driver.cannot, 'Driver');
    can(actions.primaryFleetManager.restrictedDriver, 'RestrictedDriver');
    disableBulkDriverUploadForSelfRegisteredCorporations();
    // VehicleForm
    cannot(actions.primaryFleetManager.vehicleForm, 'VehicleForm');
    // Billing
    can(actions.primaryFleetManager.billing.can, 'Billing');
    // Wallet
    can(actions.primaryFleetManager.wallet, 'Wallet');
  }

  if (corporationRole === FLEET_MANAGER) {
    can(actions.fleetManager.all, 'all');
    cannot('view', 'primary_fleet_manager');
    // Vehicle
    can(actions.fleetManager.vehicle, 'Vehicle');
    // Driver
    can(actions.fleetManager.driver.can, 'Driver');
    can(actions.fleetManager.restrictedDriver, 'RestrictedDriver');
    cannot(actions.fleetManager.driver.cannot, 'Driver');
    disableBulkDriverUploadForSelfRegisteredCorporations();
    // VehicleForm
    cannot(actions.fleetManager.vehicleForm, 'VehicleForm');
    // PaymentsSpa
    can(actions.fleetManager.paymentSpa, 'PaymentSpa');
    // Billing
    can(actions.fleetManager.billing.can, 'Billing');
    // Wallet
    can(actions.fleetManager.wallet.can, 'Wallet');
    cannot(actions.fleetManager.wallet.cannot, 'Wallet');
  }

  if (corporationRole === SERVICE_ADMIN) {
    can(actions.serviceAdmin.adminUsers, 'AdminUser');
    // Vehicle
    can(actions.serviceAdmin.vehicle, 'Vehicle');
    // Driver
    can(actions.serviceAdmin.driver.can, 'Driver');
    can(actions.serviceAdmin.restrictedDriver, 'RestrictedDriver');
    cannot(actions.serviceAdmin.driver.cannot, 'Driver');
    disableBulkDriverUploadForSelfRegisteredCorporations();
    // VehicleForm
    cannot(actions.serviceAdmin.vehicleForm, 'VehicleForm');
    // PaymentsSpa
    cannot(actions.serviceAdmin.paymentSpa.cannot, 'PaymentSpa');
    // Billing
    cannot(actions.serviceAdmin.billing.cannot, 'Billing');
    cannot(
      actions.serviceAdmin.subscriptionInvoicesAnnouncement.cannot,
      'SubscriptionInvoicesAnnouncement',
    );
  }

  if (corporationRole === REPORTING) {
    can(actions.reporting.adminUsers, 'AdminUser');

    cannot(actions.reporting.driver.cannot, 'Driver');
    cannot(actions.reporting.vehicleForm, 'VehicleForm');
    cannot(actions.reporting.vehicle.cannot, 'Vehicle');
    cannot(actions.reporting.paymentSpa.cannot, 'PaymentSpa');
    cannot(actions.reporting.billing.cannot, 'Billing');
    disableBulkDriverUploadForSelfRegisteredCorporations(REPORTING);
    cannot(
      actions.reporting.subscriptionInvoicesAnnouncement.cannot,
      'SubscriptionInvoicesAnnouncement',
    );
  }

  if (corporationRole === PBP_CUSTOMER_SUCCESS) {
    can(actions.pbpCustomerSuccess.all, 'all');
    cannot(actions.pbpCustomerSuccess.corporation.cannot, 'Corporation');
    cannot(actions.pbpCustomerSuccess.driver.cannot, 'Driver');
    // Vehicle Form
    can(actions.pbpCustomerSuccess.vehicleForm, 'VehicleForm');
    // Funding Request
    can(actions.pbpCustomerSuccess.fundingRequest, 'FundingRequest');
    // Wallet
    cannot(actions.pbpCustomerSuccess.wallet.cannot, 'Wallet');
  }

  if (corporationRole === PBP_CS_TIER_2) {
    can(actions.pbpCustomerSuccessTierTwo.all, 'all');
    cannot(actions.pbpCustomerSuccessTierTwo.corporation.cannot, 'Corporation');
    cannot(actions.pbpCustomerSuccessTierTwo.driver.cannot, 'Driver');
    // Vehicle Form
    can(actions.pbpCustomerSuccessTierTwo.vehicleForm, 'VehicleForm');
    cannot(actions.pbpCustomerSuccessTierTwo.paymentSpa.cannot, 'PaymentSpa');
    cannot(actions.pbpCustomerSuccessTierTwo.smsSettings.cannot, 'smsSettings');
    cannot(actions.pbpCustomerSuccessTierTwo.newAccount.cannot, 'newAccount');
    // Funding Request
    can(actions.pbpCustomerSuccessTierTwo.fundingRequest, 'FundingRequest');
    // Wallet
    cannot(actions.pbpCustomerSuccessTierTwo.wallet.cannot, 'Wallet');
  }

  return rules;
};
